// src/pages/Public/FAQ.jsx
import React, { useState } from "react";
import { Container, Row, Col, Card, Accordion } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiTruck,
  FiClock,
  FiDollarSign,
  FiShield,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      title: "📦 Expédition et Transport",
      icon: FiTruck,
      questions: [
        {
          q: "Quels sont les délais de livraison ?",
          a: "Les délais varient selon le mode de transport choisi :\n• Transport aérien : 5 à 10 jours ouvrés\n• Transport maritime : 45 à 90 jours ouvrés\nLes délais peuvent varier en fonction des formalités douanières et des conditions météorologiques.",
        },
        {
          q: "Comment suivre mon colis ?",
          a: "Vous pouvez suivre votre colis en temps réel via notre page de tracking. Entrez simplement votre numéro de suivi (format CACXXXXXXXX) dans le champ prévu à cet effet sur notre site. Vous recevrez également des notifications par email et WhatsApp à chaque étape importante.",
        },
        {
          q: "Quels sont les pays desservis ?",
          a: "Nous desservons actuellement 5 pays africains :\n• Gabon 🇬🇦\n• Mali 🇲🇱\n• Congo 🇨🇬\n• Côte d'Ivoire 🇨🇮\n• Tanzanie 🇹🇿\nNous étendons progressivement notre réseau à d'autres pays.",
        },
        {
          q: "Quelle est la différence entre transport aérien et maritime ?",
          a: "Le transport aérien est plus rapide (5-10 jours) mais plus coûteux, idéal pour les marchandises urgentes ou de petite taille. Le transport maritime est plus économique (45-90 jours) et adapté aux volumes importants.",
        },
      ],
    },
    {
      title: "💰 Tarifs et Paiement",
      icon: FiDollarSign,
      questions: [
        {
          q: "Comment sont calculés les tarifs ?",
          a: "Nos tarifs sont calculés en fonction de plusieurs critères :\n• Le mode de transport (aérien ou maritime)\n• Le poids et le volume de la marchandise\n• La destination\n• La nature des marchandises\nContactez-nous pour un devis personnalisé.",
        },
        {
          q: "Quels sont les moyens de paiement acceptés ?",
          a: "Nous acceptons les modes de paiement suivants :\n• Virement bancaire\n• Mobile Money (Orange Money, MTN Mobile Money)\n• Paiement en espèce (dans nos bureaux)\n• Carte bancaire (via notre plateforme sécurisée)",
        },
        {
          q: "Y a-t-il des frais supplémentaires ?",
          a: "Les frais supplémentaires peuvent inclure :\n• Les frais de douane (selon le pays de destination)\n• L'assurance (optionnelle)\n• Les frais de stockage en cas de retard de retrait\n• Les frais de livraison à domicile (selon la zone)",
        },
      ],
    },
    {
      title: "📋 Documents et Douane",
      icon: FiShield,
      questions: [
        {
          q: "Quels documents sont nécessaires pour expédier ?",
          a: "Les documents requis sont :\n• Une pièce d'identité (passeport ou CNI)\n• La facture commerciale (pour les marchandises)\n• Le packing list (contenu du colis)\n• Le numéro de TVA (pour les professionnels)\nNos équipes vous accompagnent dans la préparation des documents.",
        },
        {
          q: "Comment se passe le dédouanement ?",
          a: "Nous prenons en charge l'ensemble des formalités douanières. Nos agents expérimentés s'occupent des déclarations et du suivi des dossiers pour garantir un passage rapide des marchandises.",
        },
        {
          q: "Y a-t-il des produits interdits ?",
          a: "Certains produits sont interdits ou réglementés :\n• Armes et munitions\n• Drogues et substances illicites\n• Animaux vivants\n• Produits périssables (sans conservation adéquate)\n• Matières dangereuses\nConsultez-nous pour plus de détails.",
        },
      ],
    },
    {
      title: "🛠️ Services et Assistance",
      icon: FiPhone,
      questions: [
        {
          q: "Que faire en cas de problème avec ma livraison ?",
          a: "En cas de problème, vous pouvez :\n• Contacter notre service client au +237 612345678\n• Utiliser notre formulaire de contact\n• Nous envoyer un email à support@chineafriquecargo.com\n• Utiliser notre chat WhatsApp pour une réponse rapide\nNotre équipe est disponible 7j/7 pour vous assister.",
        },
        {
          q: "Proposez-vous une assurance pour les colis ?",
          a: "Oui, nous proposons une assurance optionnelle couvrant :\n• La perte totale ou partielle\n• Les dommages occasionnés pendant le transport\n• Le vol\nLe montant de l'assurance est calculé en fonction de la valeur déclarée de la marchandise.",
        },
        {
          q: "Puis-je modifier ou annuler ma commande ?",
          a: "Vous pouvez modifier ou annuler votre commande dans les 24h suivant l'enregistrement. Passé ce délai, la marchandise est en cours de traitement et des frais d'annulation peuvent s'appliquer. Contactez-nous directement pour toute modification.",
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - CAC | Foire Aux Questions</title>
        <meta
          name="description"
          content="Trouvez toutes les réponses à vos questions sur l'expédition, les tarifs, la douane et nos services chez CAC - Chine Afrique Cargo."
        />
        <link rel="canonical" href={window.location.origin + "/faq"} />
      </Helmet>

      <div className="faq-page">
        {/* Hero Section */}
        <div className="faq-hero">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={8}>
                <h1 className="faq-hero-title">❓ Foire Aux Questions</h1>
                <p className="faq-hero-subtitle">
                  Retrouvez toutes les réponses aux questions les plus
                  fréquentes sur nos services
                </p>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="faq-container py-5">
          <Row>
            <Col lg={3} className="mb-4">
              <div className="faq-sidebar">
                <h5 className="fw-bold mb-3">Catégories</h5>
                <div className="faq-category-list">
                  {faqCategories.map((cat, idx) => (
                    <button
                      key={idx}
                      className="faq-category-btn"
                      onClick={() => {
                        const element = document.getElementById(
                          `category-${idx}`,
                        );
                        if (element)
                          element.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                      }}
                    >
                      <cat.icon size={18} className="me-2" />
                      {cat.title}
                    </button>
                  ))}
                </div>
              </div>
            </Col>
            <Col lg={9}>
              {faqCategories.map((category, catIndex) => (
                <div
                  key={catIndex}
                  id={`category-${catIndex}`}
                  className="faq-category-section mb-5"
                >
                  <h3 className="faq-category-title">
                    <category.icon size={28} className="me-2" />
                    {category.title}
                  </h3>
                  <div className="faq-accordion">
                    {category.questions.map((item, qIndex) => {
                      const globalIndex = `${catIndex}-${qIndex}`;
                      const isOpen = openIndex === globalIndex;
                      return (
                        <div key={qIndex} className="faq-item">
                          <button
                            className="faq-question"
                            onClick={() => toggleAccordion(globalIndex)}
                          >
                            <span>{item.q}</span>
                            {isOpen ? (
                              <FiChevronUp size={20} />
                            ) : (
                              <FiChevronDown size={20} />
                            )}
                          </button>
                          <div className={`faq-answer ${isOpen ? "open" : ""}`}>
                            <div className="faq-answer-content">
                              {item.a.split("\n").map((line, i) => (
                                <p key={i}>{line}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Contact CTA */}
              <div className="faq-cta">
                <h4>Vous n'avez pas trouvé votre réponse ?</h4>
                <p className="text-muted">
                  Notre équipe est là pour vous aider
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <a
                    href="/contact"
                    className="btn btn-danger px-4 py-2 rounded-pill"
                  >
                    📞 Nous contacter
                  </a>
                  <a
                    href="https://wa.me/237612345678"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success px-4 py-2 rounded-pill"
                  >
                    💬 WhatsApp
                  </a>
                </div>
              </div>
            </Col>
          </Row>
        </Container>

        <style>{`
                    .faq-page {
                        background: #f8f9fa;
                        min-height: 100vh;
                    }
                    
                    .faq-hero {
                        background: linear-gradient(135deg, #0a0a2a 0%, #1a1a4e 50%, #0a0a2a 100%);
                        padding: 80px 0 60px;
                        margin-bottom: 0;
                    }
                    
                    .faq-hero-title {
                        font-size: 48px;
                        font-weight: 800;
                        color: white;
                        margin-bottom: 16px;
                    }
                    
                    .faq-hero-subtitle {
                        font-size: 18px;
                        color: rgba(255, 255, 255, 0.7);
                        max-width: 600px;
                        margin: 0 auto;
                    }
                    
                    .faq-sidebar {
                        background: white;
                        border-radius: 16px;
                        padding: 24px;
                        position: sticky;
                        top: 20px;
                        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                    }
                    
                    .faq-category-list {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    
                    .faq-category-btn {
                        display: flex;
                        align-items: center;
                        padding: 12px 16px;
                        border: none;
                        background: transparent;
                        border-radius: 10px;
                        font-weight: 500;
                        color: #555;
                        transition: all 0.3s ease;
                        cursor: pointer;
                        text-align: left;
                        width: 100%;
                    }
                    
                    .faq-category-btn:hover {
                        background: #f0f0f0;
                        color: #C62828;
                    }
                    
                    .faq-category-section {
                        background: white;
                        border-radius: 16px;
                        padding: 30px;
                        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                    }
                    
                    .faq-category-title {
                        font-size: 24px;
                        font-weight: 700;
                        margin-bottom: 24px;
                        display: flex;
                        align-items: center;
                        color: #1a1a3e;
                    }
                    
                    .faq-accordion {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    
                    .faq-item {
                        border: 1px solid #e9ecef;
                        border-radius: 12px;
                        overflow: hidden;
                        transition: all 0.3s ease;
                    }
                    
                    .faq-item:hover {
                        border-color: #C62828;
                    }
                    
                    .faq-question {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;
                        padding: 16px 20px;
                        background: white;
                        border: none;
                        font-size: 16px;
                        font-weight: 600;
                        color: #1a1a3e;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        text-align: left;
                    }
                    
                    .faq-question:hover {
                        background: #f8f9fa;
                    }
                    
                    .faq-answer {
                        max-height: 0;
                        overflow: hidden;
                        transition: max-height 0.4s ease, padding 0.4s ease;
                        background: #f8f9fa;
                    }
                    
                    .faq-answer.open {
                        max-height: 500px;
                    }
                    
                    .faq-answer-content {
                        padding: 0 20px 20px 20px;
                    }
                    
                    .faq-answer-content p {
                        margin-bottom: 8px;
                        color: #555;
                        line-height: 1.6;
                    }
                    
                    .faq-answer-content p:last-child {
                        margin-bottom: 0;
                    }
                    
                    .faq-cta {
                        text-align: center;
                        background: white;
                        border-radius: 16px;
                        padding: 40px;
                        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                    }
                    
                    @media (max-width: 992px) {
                        .faq-hero-title {
                            font-size: 36px;
                        }
                        
                        .faq-sidebar {
                            display: none;
                        }
                    }
                    
                    @media (max-width: 768px) {
                        .faq-hero-title {
                            font-size: 28px;
                        }
                        
                        .faq-category-section {
                            padding: 20px;
                        }
                        
                        .faq-question {
                            font-size: 14px;
                            padding: 14px 16px;
                        }
                    }
                `}</style>
      </div>
    </>
  );
};

export default FAQ;
